import { useEffect, useState, type ChangeEvent } from 'react';
import { api, type RodzajZalacznika, type Zalacznik } from '../api';
import { przygotujZdjecie, rozmiar, zwolnijPodglad, type PrzygotowaneZdjecie } from '../zdjecia';
import { Komunikat, Przycisk } from './ui';

/**
 * Zdjęcia — dwa tryby, bo są dwie różne chwile.
 *
 * `WyborZdjec` działa PRZED zapisem, gdy właściciela jeszcze nie ma: trzyma zdjęcia
 * w pamięci formularza i oddaje je rodzicowi, który wysyła je po założeniu karty.
 * `GaleriaZalacznikow` działa PO zapisie: czyta z serwera, dokłada i kasuje od razu.
 *
 * Rozdzielenie bierze się z modelu: załącznik musi wskazywać właściciela, a karta
 * urządzenia nie istnieje, dopóki formularz nie zostanie wysłany.
 */

export const NAZWY_ZALACZNIKOW: Record<RodzajZalacznika, string> = {
  ZDJECIE_OGOLNE: 'Zdjęcie ogólne',
  TABLICZKA: 'Tabliczka znamionowa',
  PO_NAPRAWIE: 'Po naprawie',
  DTR: 'DTR / instrukcja',
  PARTS_BOOK: 'Katalog części',
};

export interface ZdjecieWFormularzu extends PrzygotowaneZdjecie {
  kind: RodzajZalacznika;
}

/** Zdjęcia wymagane przy zakładaniu karty (docs/model-danych.md §3.2). */
export const ZDJECIA_KARTY: RodzajZalacznika[] = ['ZDJECIE_OGOLNE', 'TABLICZKA'];

/**
 * Wysyła zdjęcia zebrane w formularzu, gdy właściciel już istnieje.
 *
 * Zdjęcia idą PO utworzeniu karty i **nie wywracają jej**, gdy wysyłka się nie uda.
 * Karta z brakującym zdjęciem jest lepsza niż wizyta, po której nie ma nic —
 * a brakujące zdjęcie widać na karcie i da się dorobić przy kolejnej wizycie
 * (zasada 4). Zwraca liczbę tych, które nie przeszły.
 */
export async function wyslijZdjecia(
  ownerType: Zalacznik['ownerType'],
  ownerId: string,
  zdjecia: ZdjecieWFormularzu[],
): Promise<number> {
  let nieudane = 0;

  for (const zdjecie of zdjecia) {
    try {
      await api.dodajZalacznik({
        ownerType,
        ownerId,
        kind: zdjecie.kind,
        mime: zdjecie.mime,
        dane: zdjecie.dane,
        szerokosc: zdjecie.szerokosc || undefined,
        wysokosc: zdjecie.wysokosc || undefined,
      });
    } catch {
      nieudane += 1;
    } finally {
      zwolnijPodglad(zdjecie.podglad);
    }
  }

  return nieudane;
}

/** Zdjęcia zbierane w formularzu, przed założeniem karty. */
export function WyborZdjec({
  zdjecia,
  onZmiana,
  rodzaje,
  wymagane = false,
}: {
  zdjecia: ZdjecieWFormularzu[];
  onZmiana: (zdjecia: ZdjecieWFormularzu[]) => void;
  /** Które rodzaje pokazujemy jako osobne pola. */
  rodzaje: RodzajZalacznika[];
  /** Czy brak zdjęcia blokuje zapis — z ustawień firmy, nie z kodu. */
  wymagane?: boolean;
}) {
  const [blad, setBlad] = useState<string | null>(null);
  const [zajete, setZajete] = useState<RodzajZalacznika | null>(null);

  async function wybierz(kind: RodzajZalacznika, zdarzenie: ChangeEvent<HTMLInputElement>) {
    const plik = zdarzenie.target.files?.[0];
    // Pole czyścimy zawsze: bez tego wybranie tego samego pliku drugi raz nie
    // wywołuje zdarzenia i wygląda jak zawieszenie.
    zdarzenie.target.value = '';
    if (!plik) return;

    setZajete(kind);
    setBlad(null);
    try {
      const przygotowane = await przygotujZdjecie(plik);
      const poprzednie = zdjecia.find((z) => z.kind === kind);
      if (poprzednie) zwolnijPodglad(poprzednie.podglad);

      onZmiana([...zdjecia.filter((z) => z.kind !== kind), { ...przygotowane, kind }]);
    } catch {
      setBlad('Nie udało się przygotować zdjęcia. Spróbuj innego pliku.');
    } finally {
      setZajete(null);
    }
  }

  function usun(kind: RodzajZalacznika) {
    const zdjecie = zdjecia.find((z) => z.kind === kind);
    if (zdjecie) zwolnijPodglad(zdjecie.podglad);
    onZmiana(zdjecia.filter((z) => z.kind !== kind));
  }

  return (
    <div>
      <div className="grid gap-13 sm:grid-cols-2">
        {rodzaje.map((kind) => {
          const zdjecie = zdjecia.find((z) => z.kind === kind);

          return (
            <div key={kind}>
              <span className="t-label mb-5 block text-n-700">
                {NAZWY_ZALACZNIKOW[kind]}
                {wymagane && !zdjecie && <span className="t-small text-n-500"> — wymagane</span>}
              </span>

              {zdjecie ? (
                <div className="border border-n-300 bg-surface">
                  <img
                    src={zdjecie.podglad}
                    alt={NAZWY_ZALACZNIKOW[kind]}
                    className="block max-h-[180px] w-full object-contain"
                  />
                  <div className="flex flex-wrap items-center gap-9 border-t border-n-100 px-10 py-7">
                    <span className="t-mono-data mr-auto text-n-500">
                      {zdjecie.szerokosc}×{zdjecie.wysokosc} · {rozmiar(zdjecie.bytes)}
                    </span>
                    <Przycisk type="button" onClick={() => usun(kind)}>
                      Usuń
                    </Przycisk>
                  </div>
                </div>
              ) : (
                <label className="flex min-h-44 cursor-pointer items-center gap-10 border border-n-300 bg-surface px-13 py-9 hover:bg-n-50">
                  {/*
                    `capture="environment"` otwiera na telefonie tylny aparat od razu,
                    zamiast najpierw pytać o źródło. Na biurku atrybut jest ignorowany
                    i zostaje zwykły wybór pliku.
                  */}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="sr-only"
                    onChange={(zdarzenie) => void wybierz(kind, zdarzenie)}
                  />
                  <span className="t-body text-n-700">
                    {zajete === kind ? 'Przygotowuję…' : 'Zrób zdjęcie albo wybierz plik'}
                  </span>
                </label>
              )}
            </div>
          );
        })}
      </div>

      {blad && (
        <div className="mt-13">
          <Komunikat tresc={blad} />
        </div>
      )}
    </div>
  );
}

/** Załączniki istniejącego właściciela — czytane i zmieniane od razu na serwerze. */
export function GaleriaZalacznikow({
  ownerType,
  ownerId,
  rodzaje,
  mozeDodawac,
  mozeKasowac,
}: {
  ownerType: Zalacznik['ownerType'];
  ownerId: string;
  rodzaje: RodzajZalacznika[];
  mozeDodawac: boolean;
  mozeKasowac: boolean;
}) {
  const [zalaczniki, setZalaczniki] = useState<Zalacznik[]>([]);
  const [blad, setBlad] = useState<string | null>(null);
  const [zajete, setZajete] = useState(false);

  useEffect(() => {
    api
      .zalaczniki(ownerType, ownerId)
      .then((wynik) => setZalaczniki(wynik.zalaczniki))
      .catch(() => setBlad('Nie udało się wczytać załączników.'));
  }, [ownerType, ownerId]);

  async function dodaj(kind: RodzajZalacznika, zdarzenie: ChangeEvent<HTMLInputElement>) {
    const plik = zdarzenie.target.files?.[0];
    zdarzenie.target.value = '';
    if (!plik) return;

    setZajete(true);
    setBlad(null);
    try {
      const przygotowane = await przygotujZdjecie(plik);
      zwolnijPodglad(przygotowane.podglad);

      const dodany = await api.dodajZalacznik({
        ownerType,
        ownerId,
        kind,
        mime: przygotowane.mime,
        dane: przygotowane.dane,
        szerokosc: przygotowane.szerokosc || undefined,
        wysokosc: przygotowane.wysokosc || undefined,
      });
      setZalaczniki((poprzednie) => [...poprzednie, dodany]);
    } catch {
      setBlad('Nie udało się wysłać zdjęcia.');
    } finally {
      setZajete(false);
    }
  }

  async function usun(id: string) {
    setZajete(true);
    try {
      const wynik = await api.usunZalacznik(id);
      setZalaczniki(wynik.zalaczniki);
    } catch {
      setBlad('Nie udało się skasować załącznika.');
    } finally {
      setZajete(false);
    }
  }

  return (
    <div>
      {zalaczniki.length === 0 ? (
        <p className="t-small mb-13 text-n-600">
          Brak zdjęć. Zdjęcie tabliczki bywa cenniejsze od przepisanych z niej danych.
        </p>
      ) : (
        <div className="mb-13 grid gap-13 sm:grid-cols-2 lg:grid-cols-3">
          {zalaczniki.map((zalacznik) => (
            <figure key={zalacznik.id} className="border border-n-100 bg-surface">
              <PodgladZalacznika zalacznik={zalacznik} />
              <figcaption className="flex flex-wrap items-center gap-9 border-t border-n-100 px-10 py-7">
                <span className="t-small mr-auto text-n-700">
                  {NAZWY_ZALACZNIKOW[zalacznik.kind]}
                </span>
                <span className="t-mono-data text-n-500">{rozmiar(zalacznik.bytes)}</span>
                {mozeKasowac && (
                  <Przycisk type="button" disabled={zajete} onClick={() => void usun(zalacznik.id)}>
                    Usuń
                  </Przycisk>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {mozeDodawac && (
        <div className="flex flex-wrap items-center gap-9">
          {rodzaje.map((kind) => (
            <label
              key={kind}
              className="flex min-h-44 cursor-pointer items-center border border-n-300 bg-surface px-13 hover:bg-n-50"
            >
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                onChange={(zdarzenie) => void dodaj(kind, zdarzenie)}
              />
              <span className="t-label text-n-700">
                {zajete ? 'Wysyłam…' : `+ ${NAZWY_ZALACZNIKOW[kind]}`}
              </span>
            </label>
          ))}
        </div>
      )}

      {blad && (
        <div className="mt-13">
          <Komunikat tresc={blad} />
        </div>
      )}
    </div>
  );
}

/** Miniatura pobierana z tokenem — `<img src>` sam by go nie wysłał. */
function PodgladZalacznika({ zalacznik }: { zalacznik: Zalacznik }) {
  const [adres, setAdres] = useState<string | null>(null);

  useEffect(() => {
    let aktualne = true;
    let obiekt: string | null = null;

    api
      .plikZalacznika(zalacznik.id)
      .then((url) => {
        obiekt = url;
        if (aktualne) setAdres(url);
        else URL.revokeObjectURL(url);
      })
      .catch(() => undefined);

    return () => {
      aktualne = false;
      if (obiekt) URL.revokeObjectURL(obiekt);
    };
  }, [zalacznik.id]);

  if (zalacznik.mime === 'application/pdf') {
    return (
      <div className="flex h-[160px] items-center justify-center bg-n-50">
        <span className="t-mono-data text-n-600">PDF</span>
      </div>
    );
  }

  return adres ? (
    <img
      src={adres}
      alt={NAZWY_ZALACZNIKOW[zalacznik.kind]}
      className="block max-h-[160px] w-full object-contain"
    />
  ) : (
    <div className="h-[160px] bg-n-50" />
  );
}
